import React, { useState, useEffect } from 'react';
import { updateProfile } from '../../../services/candidate/profileService';
import { toast } from 'react-toastify';

const PersonalInfo = ({ profileData, onUpdate }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    location: {
      city: '',
      state: '',
      pincode: ''
    },
    gender: '',
    professionalTitle: '',
    professionalSummary: '',
    socialMedia: {
      linkedin: '',
      github: '',
      portfolio: ''
    }
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(null);  

  // Initialize form data when profileData changes
  useEffect(() => {
    if (profileData?.profile) {
      const profile = profileData.profile;
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        phone: profile.phone || '',
        location: {
          city: profile.location?.city || '',
          state: profile.location?.state || '',
          pincode: profile.location?.pincode || ''
        },
        gender: profile.gender || '',
        professionalTitle: profile.professionalTitle || '',
        professionalSummary: profile.professionalSummary || '',
        socialMedia: {
          linkedin: profile.socialMedia?.linkedin || '',
          github: profile.socialMedia?.github || '',
          portfolio: profile.socialMedia?.portfolio || ''
        }
      });
    }
  }, [profileData]);

  // Validation rules
  const validateForm = () => {
    const newErrors = {};

    // First name validation (required)
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    } else if (formData.firstName.length > 50) {
      newErrors.firstName = 'First name must be less than 50 characters';
    }

    // Last name validation (optional but with constraints)
    if (formData.lastName && formData.lastName.length > 50) {
      newErrors.lastName = 'Last name must be less than 50 characters';
    }

    // Phone validation (optional but must be valid Indian phone number)
    if (formData.phone) {
      const phoneRegex = /^[6-9]\d{9}$/;
      if (!phoneRegex.test(formData.phone.replace(/\D/g, ''))) {
        newErrors.phone = 'Please enter a valid Indian phone number';
      }
    }

    // City validation (optional)
    if (formData.location.city && formData.location.city.length > 50) {
      newErrors.city = 'City name must be less than 50 characters';
    }

    // State validation (optional)
    if (formData.location.state && formData.location.state.length > 50) {
      newErrors.state = 'State name must be less than 50 characters';
    }

    // Pincode validation (optional but must be 6 digits)
    if (formData.location.pincode) {
      const pincodeRegex = /^\d{6}$/;
      if (!pincodeRegex.test(formData.location.pincode)) {
        newErrors.pincode = 'Pincode must be 6 digits';
      }
    }

    // Gender validation (optional but must be one of allowed values)
    if (formData.gender && !['Male', 'Female', 'Other', 'Prefer not to say'].includes(formData.gender)) {
      newErrors.gender = 'Please select a valid gender';
    }

    // Professional title validation (optional)
    if (formData.professionalTitle && formData.professionalTitle.length > 100) {
      newErrors.professionalTitle = 'Title must be less than 100 characters';
    }

    // Professional summary validation (optional)
    if (formData.professionalSummary && formData.professionalSummary.length > 1000) {
      newErrors.professionalSummary = 'Summary must be less than 1000 characters';
    }

    // LinkedIn URL validation (optional)
    if (formData.socialMedia.linkedin && !isValidUrl(formData.socialMedia.linkedin)) {
      newErrors.linkedin = 'Please enter a valid URL';
    }

    // GitHub URL validation (optional)
    if (formData.socialMedia.github && !isValidUrl(formData.socialMedia.github)) {
      newErrors.github = 'Please enter a valid URL';
    }

    // Portfolio URL validation (optional)
    if (formData.socialMedia.portfolio && !isValidUrl(formData.socialMedia.portfolio)) {
      newErrors.portfolio = 'Please enter a valid URL';
    }

    return newErrors;
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      if (parent === 'location' || parent === 'socialMedia') {
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        }));
        // Clear error for this field
        if (errors[child]) {
          setErrors(prev => ({ ...prev, [child]: '' }));
        }
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      // Clear error for this field
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
  };

  // Function called when user submits the form to update profile
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    try {
      // Prepare the payload according to API requirements
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        location: {
          city: formData.location.city,
          state: formData.location.state,
          pincode: formData.location.pincode
        },
        gender: formData.gender,
        professionalTitle: formData.professionalTitle,
        professionalSummary: formData.professionalSummary,
        socialMedia: {
          linkedin: formData.socialMedia.linkedin,
          github: formData.socialMedia.github,
          portfolio: formData.socialMedia.portfolio
        }
      };

      // Remove empty fields from payload
      Object.keys(payload).forEach(key => {
        if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      // Clean location object - remove empty values and empty object
      if (payload.location) {
        // Remove empty location fields
        Object.keys(payload.location).forEach(key => {
          if (!payload.location[key]) {
            delete payload.location[key];
          }
        });
        
        // If location object is empty after cleaning, remove it
        if (Object.keys(payload.location).length === 0) {
          delete payload.location;
        }
      }

      // Clean socialMedia object - remove empty values and empty object
      if (payload.socialMedia) {
        // Remove empty socialMedia fields
        Object.keys(payload.socialMedia).forEach(key => {
          if (!payload.socialMedia[key]) {
            delete payload.socialMedia[key];
          }
        });
        
        // If socialMedia object is empty after cleaning, remove it
        if (Object.keys(payload.socialMedia).length === 0) {
          delete payload.socialMedia;
        }
      }

      //console.log('Submitting profile data:', payload);
      const response = await updateProfile(payload);

      if (response.success) {
        toast.success('Profile updated successfully!');
        setIsEditing(false);
        
        // Update the user info in local storage also        
        const userStr = localStorage.getItem('candidate_user');
        if (userStr) {
          const userData = JSON.parse(userStr);
          
          // Update only the fields that were changed
          if (payload.firstName) {
              userData.name = payload.firstName;
          }
          if (payload.lastName) {
              userData.lastName = payload.lastName;
          }
          
          // Update fullName if either name or lastName changed
          if (payload.firstName || payload.lastName) {
              const newFirstName = payload.firstName || userData.name;
              const newLastName = payload.lastName || userData.lastName;
              userData.fullName = `${newFirstName} ${newLastName}`.trim();
          }
          
          // Save back to local storage
          localStorage.setItem('candidate_user', JSON.stringify(userData));
          
          // Optional: Trigger event for other components to listen
          window.dispatchEvent(new CustomEvent('userDataUpdated', { 
            detail: { userData }
          }));
        }

        if (onUpdate) {
          onUpdate();
        }
        
      } else {
        toast.error(response.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error updating profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Function called when user clicks "Cancel" to reset form to original profile data
  const handleCancel = () => {
    if (profileData?.profile) {
      const profile = profileData.profile;
      
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        phone: profile.phone || '',
        location: {
          city: profile.location?.city || '',
          state: profile.location?.state || '',
          pincode: profile.location?.pincode || ''
        },
        gender: profile.gender || '',
        professionalTitle: profile.professionalTitle || '',
        professionalSummary: profile.professionalSummary || '',
        socialMedia: {
          linkedin: profile.socialMedia?.linkedin || '',
          github: profile.socialMedia?.github || '',
          portfolio: profile.socialMedia?.portfolio || ''
        }
      });
    }
    setErrors({});
    setIsEditing(false);
  };

  if (!profileData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="personal-info">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Update your personal and professional details</h3>          
        </div>
        {!isEditing && 
          <button
            onClick={() => setIsEditing(true)}
            className="btn-primary text-white py-2 px-4 rounded-lg flex items-center"
          >
            <i className="fas fa-edit mr-2"></i>
            Edit Profile
          </button>
        }
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.firstName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your first name"
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.lastName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your last name"
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                maxLength={10}
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="9876543210"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
              )}
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gender
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.gender ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
              {errors.gender && (
                <p className="mt-1 text-sm text-red-500">{errors.gender}</p>
              )}
            </div>
          </div>

          {/* Professional Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Professional Title
            </label>
            <input
              type="text"
              name="professionalTitle"
              value={formData.professionalTitle}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.professionalTitle ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., Full Stack Developer"
            />
            {errors.professionalTitle && (
              <p className="mt-1 text-sm text-red-500">{errors.professionalTitle}</p>
            )}
          </div>

          {/* Professional Summary */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Professional Summary
            </label>
            <textarea
              name="professionalSummary"
              value={formData.professionalSummary}
              onChange={handleChange}
              rows="4"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.professionalSummary ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Describe your professional experience and skills..."
            />
            {errors.professionalSummary && (
              <p className="mt-1 text-sm text-red-500">{errors.professionalSummary}</p>
            )}
          </div>

          {/* Location Section */}
          <div className="border-t pt-6">
            <h4 className="text-md font-semibold text-gray-800 mb-4">Location Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <input
                  type="text"
                  name="location.city"
                  value={formData.location.city}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.city ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter city"
                />
                {errors.city && (
                  <p className="mt-1 text-sm text-red-500">{errors.city}</p>
                )}
              </div>

              {/* State */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State
                </label>
                <input
                  type="text"
                  name="location.state"
                  value={formData.location.state}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.state ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter state"
                />
                {errors.state && (
                  <p className="mt-1 text-sm text-red-500">{errors.state}</p>
                )}
              </div>

              {/* Pincode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pincode
                </label>
                <input
                  type="text"
                  name="location.pincode"
                  maxLength={6}
                  value={formData.location.pincode}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.pincode ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="560001"
                />
                {errors.pincode && (
                  <p className="mt-1 text-sm text-red-500">{errors.pincode}</p>
                )}
              </div>
            </div>
          </div>

          {/* Social Media Section */}
          <div className="border-t pt-6">
            <h4 className="text-md font-semibold text-gray-800 mb-4">Social Media Links</h4>
            <div className="space-y-4">
              {/* LinkedIn */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  LinkedIn URL
                </label>
                <input
                  type="url"
                  name="socialMedia.linkedin"
                  value={formData.socialMedia.linkedin}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.linkedin ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="https://linkedin.com/in/username"
                />
                {errors.linkedin && (
                  <p className="mt-1 text-sm text-red-500">{errors.linkedin}</p>
                )}
              </div>

              {/* GitHub */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  GitHub URL
                </label>
                <input
                  type="url"
                  name="socialMedia.github"
                  value={formData.socialMedia.github}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.github ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="https://github.com/username"
                />
                {errors.github && (
                  <p className="mt-1 text-sm text-red-500">{errors.github}</p>
                )}
              </div>

              {/* Portfolio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Portfolio URL
                </label>
                <input
                  type="url"
                  name="socialMedia.portfolio"
                  value={formData.socialMedia.portfolio}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.portfolio ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="https://yourportfolio.com"
                />
                {errors.portfolio && (
                  <p className="mt-1 text-sm text-red-500">{errors.portfolio}</p>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 btn-primary text-white py-2 px-4 rounded-lg flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Full Name</h4>
              <p className="text-gray-800">
                {profileData.profile.fullName || `${profileData.profile.firstName} ${profileData.profile.lastName}`.trim() || 'Not provided'}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Email</h4>
              <p className="text-gray-800">{profileData.profile.email || 'Not provided'}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Phone</h4>
              <p className="text-gray-800">{profileData.profile.phone || 'Not provided'}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Gender</h4>
              <p className="text-gray-800">{profileData.profile.gender || 'Not provided'}</p>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Professional Title</h4>
            <p className="text-gray-800">{profileData.profile.professionalTitle || 'Not provided'}</p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Professional Summary</h4>
            <p className="text-gray-800 whitespace-pre-line">
              {profileData.profile.professionalSummary || 'Not provided'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Location</h4>
              <p className="text-gray-800">
                {profileData.profile.location ? 
                  `${profileData.profile.location.city || ''} ${profileData.profile.location.state || ''} ${profileData.profile.location.pincode || ''}`.trim() || 'Not provided' 
                  : 'Not provided'
                }
              </p>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Social Media</h4>
            <div className="space-y-2">
              {profileData.profile.socialMedia?.linkedin && (
                <div className="flex items-center">
                  <i className="fab fa-linkedin text-blue-600 mr-2"></i>
                  <a href={profileData.profile.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    LinkedIn
                  </a>
                </div>
              )}
              {profileData.profile.socialMedia?.github && (
                <div className="flex items-center">
                  <i className="fab fa-github text-gray-800 mr-2"></i>
                  <a href={profileData.profile.socialMedia.github} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    GitHub
                  </a>
                </div>
              )}
              {profileData.profile.socialMedia?.portfolio && (
                <div className="flex items-center">
                  <i className="fas fa-globe text-green-600 mr-2"></i>
                  <a href={profileData.profile.socialMedia.portfolio} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    Portfolio
                  </a>
                </div>
              )}
              {!profileData.profile.socialMedia?.linkedin && !profileData.profile.socialMedia?.github && !profileData.profile.socialMedia?.portfolio && (
                <p className="text-gray-600">No social media links added</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalInfo;